import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePerm } from "../context/PermContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const ROUTES = [
  [/^\/$/, "Home"], [/^\/notizie/, "News"], [/^\/report\/ee/, "Letture EE"], [/^\/report\/immobili/, "Letture immobili"], [/^\/report/, "Report"],
  [/^\/turni/, "Turni"], [/^\/ferie/, "Ferie"], [/^\/avvisi/, "Avvisi"], [/^\/alert/, "Alert"], [/^\/marina/, "Mappa porto"], [/^\/meteo/, "Meteo Porto"],
  [/^\/parcheggi/, "Gestione Parcheggi"], [/^\/postit/, "Post-it"], [/^\/corsi/, "Corsi e brevetti"], [/^\/scadenziario/, "Scadenziario"],
  [/^\/manutenzioni/, "Manutenzione"], [/^\/nostromo/, "Compiti assegnati"], [/^\/profilo/, "Profilo"], [/^\/(vigilanza|comunica-vigilanza)/, "Vigilanza"],
  [/^\/admin\/shifts-view/, "Turni caricati"], [/^\/admin\/overtime/, "Straordinari"], [/^\/admin\/users/, "Gestione utenti"], [/^\/admin/, "Pannello Direttore"],
];

const EXTRA = { Ferie: ["Permessi", "Malattia"], Turni: ["Ferie", "Permessi", "Malattia", "Straordinari"], Avvisi: ["Alert"] };

function Extra({ names }) {
  const [items, setItems] = useState([]);
  useEffect(() => { Promise.all(names.map((n) => api(`/help/${encodeURIComponent(n)}`).catch(() => ({ section: n, testo: "" })))).then((r) => setItems(r.filter((x) => x.testo))); }, [names]);
  return items.map((x) => (
    <div key={x.section} className="border-t pt-3" data-testid={`help-extra-${x.section.replace(/\s/g, "-")}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--teal)] mb-1">{x.section}</p>
      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{x.testo}</p>
    </div>));
}

export function HelpButton({ dark = false }) {
  const { pathname } = useLocation();
  const section = (ROUTES.find(([re]) => re.test(pathname)) || [])[1];
  const [open, setOpen] = useState(false);
  const [testo, setTesto] = useState(null);
  const [draft, setDraft] = useState(null);
  const { admin } = usePerm();
  useEffect(() => { setTesto(null); setDraft(null); }, [section]);
  useEffect(() => {
    if (!open || !section || testo !== null) { return; }
    api(`/help/${encodeURIComponent(section)}`).then((d) => setTesto(d.testo || "")).catch(() => setTesto(""));
  }, [open, section, testo]);
  if (!section) return null;
  const save = async () => {
    try { await api(`/admin/help/${encodeURIComponent(section)}`, { method: "PUT", body: { testo: draft } }); toast.success("Testo di aiuto salvato"); setTesto(null); setDraft(null); } catch (e) { toast.error(e.message); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Aiuto" className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${dark ? "text-white/80 hover:bg-white/10" : "bg-white border shadow-sm text-[var(--navy)] hover:bg-slate-50"}`} data-testid={dark ? "help-button-mobile" : "help-button"}>
        <HelpCircle className="w-5 h-5" />
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDraft(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="help-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HelpCircle className="w-5 h-5 text-[var(--teal)]" />Aiuto · {section}</DialogTitle></DialogHeader>
          {draft !== null ? <>
            <Textarea rows={8} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="help-edit-input" />
            <p className="text-xs text-slate-500">Lascia vuoto per tornare al testo predefinito.</p>
            <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-full" onClick={() => setDraft(null)} data-testid="help-edit-cancel">Annulla</Button><Button className="rounded-full bg-[var(--teal)]" onClick={save} data-testid="help-edit-save">Salva</Button></div>
          </> : <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="help-text">{testo === null ? "Caricamento…" : testo || "Per questa pagina non è ancora disponibile un testo di aiuto."}</p>}
          {admin && draft === null && testo !== null && <div className="flex justify-end"><Button variant="outline" size="sm" className="rounded-full" onClick={() => setDraft(testo)} data-testid="help-edit-button"><Pencil className="w-4 h-4 mr-1" />Modifica testo</Button></div>}
          {draft === null && EXTRA[section] && <Extra names={EXTRA[section]} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
